import exifr from 'exifr';
import { supabase } from './supabase';
import {
  extractGpsFromParsedExif,
  normalizeNumber,
  type GpsCoordinates,
} from './gps';
import { getFileExtension, inferUploadMimeType } from './uploadMime';
import type { PhotoStatus } from '../types/contract';

interface ExifData {
  latitude: number | null;
  longitude: number | null;
  altitudeM: number | null;
  capturedAt: string | null;
  hasGps: boolean;
}

function normalizeCapturedAt(value: unknown): string | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }

  return null;
}

async function extractGpsFallback(file: File): Promise<GpsCoordinates | null> {
  try {
    const gps = await exifr.gps(file);
    return gps ? extractGpsFromParsedExif(gps as unknown as Record<string, unknown>) : null;
  } catch (error) {
    console.warn('Failed to parse GPS fallback data', error);
  }

  return null;
}

async function extractExif(file: File): Promise<ExifData> {
  let data: Record<string, unknown> | null = null;
  const gpsOnly = await extractGpsFallback(file);

  try {
    data = await exifr.parse(file, {
      tiff: true,
      exif: true,
      gps: true,
      xmp: true,
      mergeOutput: true,
      reviveValues: true,
      translateKeys: true,
      translateValues: true,
    }) ?? null;
  } catch (error) {
    console.warn('Failed to parse EXIF data', error);
  }

  const parsedGps = data ? extractGpsFromParsedExif(data) : null;
  const gps = gpsOnly ?? parsedGps;

  return {
    latitude: gps?.latitude ?? null,
    longitude: gps?.longitude ?? null,
    altitudeM: data ? normalizeNumber(data.GPSAltitude) : null,
    capturedAt: data ? normalizeCapturedAt(data.DateTimeOriginal) : null,
    hasGps: Boolean(gps),
  };
}

export interface UploadPhotoPipelineResult {
  photoId: string;
  photoStatus: PhotoStatus | null;
  browserFileHadReadableGps: boolean;
}

export interface UploadPhotoPipelineOptions {
  preserveOriginal?: boolean;
}

function normalizePhotoStatus(value: unknown): PhotoStatus | null {
  return value === 'ready'
    || value === 'missing_gps'
    || value === 'processing'
    || value === 'failed'
    ? value
    : null;
}

interface Derivative {
  kind: 'thumbnail' | 'preview';
  blob: Blob;
  width: number;
  height: number;
  bytes: number;
}

function generateDerivative(img: HTMLImageElement, maxWidth: number): Promise<Derivative> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const scale = Math.min(1, maxWidth / img.width);
    const targetWidth = Math.round(img.width * scale);
    const targetHeight = Math.round(img.height * scale);

    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return reject(new Error('사진 압축을 시작하지 못했습니다.'));

    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('사진 압축 결과를 만들지 못했습니다.'));
      resolve({
        kind: maxWidth <= 400 ? 'thumbnail' : 'preview',
        blob,
        width: targetWidth,
        height: targetHeight,
        bytes: blob.size,
      });
    }, 'image/jpeg', 0.85); // JPEG quality 85%
  });
}

export async function uploadPhotoPipeline(
  file: File,
  options: UploadPhotoPipelineOptions = {},
): Promise<UploadPhotoPipelineResult> {
  if (!supabase) throw new Error('Supabase client not initialized');

  let preparedPhotoId: string | null = null;
  let objectUrl: string | null = null;

  const markPreparedUploadFailed = async () => {
    if (!preparedPhotoId || !supabase) return;

    try {
      await supabase.functions.invoke('finalize-upload', {
        body: {
          photoId: preparedPhotoId,
          markFailed: true,
          assets: [],
        },
      });
    } catch {
      console.warn('Failed to mark prepared upload as failed');
    }
  };

  try {
    const uploadMimeType = inferUploadMimeType(file);
    const uploadExtension = getFileExtension(file) || (uploadMimeType === 'image/png' ? 'png' : 'jpg');
    const originalFilename = file.name || `photo-${Date.now()}.${uploadExtension}`;

    // 1. Read EXIF
    const exif = await extractExif(file);
    const includeOriginalForServerExif = options.preserveOriginal === true || !exif.hasGps;

    // 2. Load image for derivatives
    const img = new Image();
    objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;
    await new Promise((resolve, reject) => {
      img.onload = () => {
        if (!img.naturalWidth || !img.naturalHeight) {
          reject(new Error('사진 크기를 읽지 못했습니다. 다른 원본 사진을 선택해 주세요.'));
          return;
        }
        resolve(null);
      };
      img.onerror = () => {
        reject(new Error('이 사진은 휴대폰 브라우저에서 불러오지 못했습니다. 일반 JPEG 사진이나 해상도를 낮춘 사진을 선택해 주세요.'));
      };
    });

    // 3. Generate derivatives (thumbnail max 400px width, preview max 1600px width)
    let thumbnail: Derivative, preview: Derivative;
    try {
      thumbnail = await generateDerivative(img, 400);
      preview = await generateDerivative(img, 1600);
    } catch (error) {
      const detail = error instanceof Error ? error.message : '사진 압축에 실패했습니다.';
      throw new Error(`${detail} 원본 사진이 너무 크거나 브라우저에서 처리할 수 없는 형식일 수 있습니다.`);
    }

    // 4. Prepare upload via edge function
    const prepareRes = await supabase.functions.invoke('prepare-upload', {
      body: {
        originalFilename,
        mimeType: uploadMimeType,
        includeOriginal: includeOriginalForServerExif,
      },
    });

    if (prepareRes.error) throw new Error('서버 등록 준비에 실패했습니다. 로그인 상태를 확인한 뒤 다시 시도해 주세요.');
    const { photoId, uploads } = prepareRes.data;
    preparedPhotoId = photoId;

    // 5. Upload derivatives using signed URLs
    const finalizeAssets: any[] = [];
    for (const target of uploads) {
      const derivative = target.kind === 'thumbnail' ? thumbnail : target.kind === 'preview' ? preview : null;
      const uploadBody = derivative?.blob ?? (target.kind === 'original' ? file : null);
      if (!uploadBody) continue;

      // Use supabase storage to upload to the signed url
      const { error: uploadError } = await supabase.storage
        .from(target.bucket)
        .uploadToSignedUrl(target.path, target.token, uploadBody, {
          contentType: target.kind === 'original' ? uploadMimeType : 'image/jpeg',
        });

      if (uploadError) throw new Error('사진 업로드에 실패했습니다. 네트워크 상태를 확인한 뒤 다시 시도해 주세요.');

      finalizeAssets.push({
        kind: target.kind,
        bucket: target.bucket,
        path: target.path,
        width: derivative?.width ?? img.naturalWidth,
        height: derivative?.height ?? img.naturalHeight,
        bytes: derivative?.bytes ?? file.size,
      });
    }

    // 6. Finalize upload
    const finalizeRes = await supabase.functions.invoke('finalize-upload', {
      body: {
        photoId,
        capturedAt: exif.capturedAt,
        latitude: exif.latitude,
        longitude: exif.longitude,
        altitudeM: exif.altitudeM,
        hasGps: exif.hasGps,
        assets: finalizeAssets,
        markFailed: false,
      },
    });

    if (finalizeRes.error) throw new Error('서버 저장 완료 처리에 실패했습니다. 압축 사진 검증 또는 위치 정보 저장 단계에서 실패했습니다.');

    return {
      photoId,
      photoStatus: normalizePhotoStatus((finalizeRes.data as { photo_status?: unknown } | null)?.photo_status),
      browserFileHadReadableGps: exif.hasGps,
    };
  } catch (error) {
    await markPreparedUploadFailed();
    throw error;
  } finally {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}
